import dayjs, { type Dayjs } from 'dayjs';
import {
  ArrowUpRight,
  CalendarClock,
  ClipboardCheck,
  Columns3,
  FileClock,
  FileText,
  ListChecks,
} from 'lucide-react';
import { lazy, Suspense, useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { classFor } from '../../shared/lib/class-map';
import { cn } from '../../shared/lib/cn';
import { Badge } from '../../shared/ui/Badge';
import { Button } from '../../shared/ui/Button';
import { IconButton } from '../../shared/ui/IconButton';
import { Input } from '../../shared/ui/Input';

import type { EventColor } from './activity-format';
import { DocumentReviewActions } from './DocumentReviewActions';
import { EventIcon } from './EventIcon';
import type { InstituteMemberRow } from './staff-api';
import { SKILL_LABELS } from './work-filters';
import {
  canOwn,
  canSnooze,
  canTick,
  describeDue,
  isLate,
  snoozeToDate,
  workContext,
  workLink,
} from './work-queue';
import type { WorkItem } from './workflow-api';
import s from './WorkQueueRow.module.css';

// Lazy on purpose: the dashboard renders this row in compact mode (no
// snooze) from an EAGER route, and the Radix menu behind the snooze button is
// the only thing here that would otherwise join the initial bundle.
const SnoozeMenu = lazy(() => import('./SnoozeMenu').then((m) => ({ default: m.SnoozeMenu })));

interface Props {
  row: WorkItem;
  now: Dayjs;
  staff: readonly InstituteMemberRow[];
  /** For "Take it". Null hides it. */
  currentUserId: string | null;
  busy: boolean;
  /**
   * The dashboard preview: tick, badges, date and the link — no owner control,
   * no snooze, no review in place. Everything else is one click away on Work.
   */
  compact?: boolean;
  onTick: (row: WorkItem) => void;
  onAssign: (row: WorkItem, assigneeId: string | null) => void;
  onSnooze: (row: WorkItem, dueAt: string) => void;
  onOpenFile: (row: WorkItem) => void;
  onReview: (row: WorkItem, accept: boolean, note: string | null) => void;
}

const DOT_CLASS: Record<EventColor, string> = {
  slate: s.slate ?? '',
  teal: s.teal ?? '',
  blue: s.blue ?? '',
  green: s.green ?? '',
  amber: s.amber ?? '',
  rose: s.rose ?? '',
  violet: s.violet ?? '',
};

/**
 * One row of the work queue (phase 174).
 *
 * The 95% case is still a glance and a tick, so the row reads left to right
 * as: can I close this → who → what → whose court is it in → when → open.
 * Reassigning and snoozing sit on the row because sending someone to the card
 * to change a date is how a queue stops being used. A file to review carries
 * its three actions underneath, so the queue IS the review screen.
 */
export function WorkQueueRow({
  row,
  now,
  staff,
  currentUserId,
  busy,
  compact = false,
  onTick,
  onAssign,
  onSnooze,
  onOpenFile,
  onReview,
}: Props): JSX.Element {
  const fid = useId();
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState('');

  const late = isLate(row, now);
  const context = workContext(row);
  const tickable = canTick(row);
  const snoozable = !compact && canSnooze(row);
  const ownable = !compact && canOwn(row);
  const knownAssignee =
    row.assignee_id === null || staff.some((member) => member.user_id === row.assignee_id);

  const what =
    row.kind === 'review'
      ? `sent “${row.title}”`
      : row.title + (context !== null ? ` · ${context}` : '');
  const snoozeLabel = `${row.title} for ${row.student_name}`;

  return (
    <li
      className={cn(
        s.root,
        late && s.late,
        (row.kind === 'review' || row.kind === 'grading') && s.reviewRow,
      )}
    >
      <div className={s.lead}>
        {tickable ? (
          <input
            type="checkbox"
            className={s.tick}
            checked={false}
            disabled={busy}
            aria-label={`Mark "${row.title}" for ${row.student_name} done`}
            onChange={() => {
              onTick(row);
            }}
          />
        ) : (
          <span className={s.mark} aria-hidden="true">
            {row.kind === 'review' ? (
              <FileClock size={13} />
            ) : row.kind === 'grading' ? (
              <ClipboardCheck size={13} />
            ) : row.kind === 'assignment' ? (
              <FileText size={13} />
            ) : row.kind === 'checklist' ? (
              <ListChecks size={13} />
            ) : (
              <Columns3 size={13} />
            )}
          </span>
        )}
        <span className={classFor(DOT_CLASS, row.color)} aria-hidden="true">
          <EventIcon name={row.icon} size={13} />
        </span>
      </div>

      <div className={s.main}>
        <div className={s.line}>
          <Link to={`/institute/students/${row.student_id}/dossier`} className={s.student}>
            {row.student_name}
          </Link>
          <span className={s.what}>{what}</span>
        </div>

        <div className={s.meta}>
          {row.kind === 'review' ? <Badge tone="warning">To review</Badge> : null}
          {row.kind === 'grading' ? <Badge tone="warning">To grade</Badge> : null}
          {row.kind === 'assignment' ? <Badge tone="neutral">Not handed in</Badge> : null}
          {/* The skills a grading row is actually waiting on. Named, not
              colour-coded: "Writing" is the instruction. */}
          {row.skills.map((skill) => (
            <Badge key={skill} tone="info">
              {/* eslint-disable-next-line security/detect-object-injection --
                  `skill` is the WorkSkill literal union and the record is
                  complete over it */}
              {SKILL_LABELS[skill]}
            </Badge>
          ))}
          {row.is_unassigned && row.kind !== 'assignment' ? (
            <Badge tone="neutral">Unassigned</Badge>
          ) : null}
          {row.waiting_on_student && row.kind !== 'assignment' ? (
            <Badge tone="info">Waiting on student</Badge>
          ) : null}
          {row.submission_pending && row.kind !== 'review' ? (
            <Badge tone="warning">File to review</Badge>
          ) : null}
          {row.blocked_reason !== null ? (
            <Badge tone="warning" title={row.blocked_reason}>
              Blocked
              {row.blocked_until !== null
                ? ` until ${dayjs(row.blocked_until).format('D MMM')}`
                : ''}
            </Badge>
          ) : null}

          {ownable ? (
            <span className={s.owner}>
              {row.is_unassigned && currentUserId !== null ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    onAssign(row, currentUserId);
                  }}
                >
                  Take it
                </Button>
              ) : null}
              <select
                className={s.ownerSelect}
                aria-label={`Who is doing: ${row.title} for ${row.student_name}`}
                value={row.assignee_id ?? ''}
                disabled={busy}
                onChange={(event) => {
                  onAssign(row, event.target.value === '' ? null : event.target.value);
                }}
              >
                <option value="">Nobody yet</option>
                {staff.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.display_name}
                  </option>
                ))}
                {!knownAssignee && row.assignee_id !== null ? (
                  <option value={row.assignee_id}>
                    {row.assignee_name ?? 'Someone who has left'}
                  </option>
                ) : null}
              </select>
            </span>
          ) : compact && row.assignee_name !== null ? (
            <span className={s.ownerName}>{row.assignee_name}</span>
          ) : null}
        </div>
      </div>

      <div className={s.side}>
        <span className={late ? s.dueLate : s.due}>{describeDue(row, now)}</span>

        {snoozable ? (
          <Suspense
            fallback={
              <IconButton label={`Snooze ${snoozeLabel}`} disabled>
                <CalendarClock size={14} aria-hidden="true" />
              </IconButton>
            }
          >
            <SnoozeMenu
              label={snoozeLabel}
              now={now}
              disabled={busy}
              onSnooze={(dueAt) => {
                onSnooze(row, dueAt);
              }}
              onPickDate={() => {
                setPicking(true);
              }}
            />
          </Suspense>
        ) : null}

        <Link
          to={workLink(row)}
          className={s.open}
          aria-label={`Open ${row.title} for ${row.student_name}`}
        >
          <ArrowUpRight size={14} aria-hidden="true" />
        </Link>
      </div>

      {picking ? (
        <form
          className={s.picker}
          onSubmit={(event) => {
            event.preventDefault();
            const iso = snoozeToDate(picked);
            if (iso === null) return;
            onSnooze(row, iso);
            setPicking(false);
            setPicked('');
          }}
        >
          <label htmlFor={`${fid}-date`} className={s.pickerLabel}>
            Snooze until
          </label>
          <Input
            id={`${fid}-date`}
            type="date"
            value={picked}
            min={now.format('YYYY-MM-DD')}
            onChange={(event) => {
              setPicked(event.target.value);
            }}
          />
          <Button type="submit" size="sm" disabled={snoozeToDate(picked) === null}>
            Set
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setPicking(false);
              setPicked('');
            }}
          >
            Cancel
          </Button>
        </form>
      ) : null}

      {row.kind === 'review' && !compact ? (
        <div className={s.review}>
          <DocumentReviewActions
            label={row.title}
            storagePath={row.storage_path}
            externalUrl={row.external_url}
            busy={busy}
            onOpen={() => {
              onOpenFile(row);
            }}
            onAccept={() => {
              onReview(row, true, null);
            }}
            onReject={(reason) => {
              onReview(row, false, reason);
            }}
          />
        </div>
      ) : null}
    </li>
  );
}
